import type { CSSProperties, HTMLAttributes, ReactElement } from "react";
import { Link, useNavigate } from "react-router";
import { List } from "react-window";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Modal,
  TextField,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import type { FuseResult, FuseResultMatch } from "fuse.js";
import { useTrackFeature } from "src/business-hooks/useTrackFeature";
import type {
  EntityTypeOption,
  SearchEntity,
  TaskPayload,
} from "src/queries/search/search.types";
import type { RecentObject } from "src/storage/recentObjects.types.ts";
import GlobalSearchAssetChip from "./components/GlobalSearchAssetChip/GlobalSearchAssetChip";
import { SEARCH_MODAL } from "./SearchModal.constants";
import { highlightMatches } from "./SearchModal.highlight";
import { useSearchModal, useSearchModalLogic } from "./SearchModal.hooks";
import { getEntityUrl } from "./SearchModal.utils";

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
}

type SearchOption = FuseResult<SearchEntity> | FuseResult<EntityTypeOption>;

const isEntityTypeOption = (
  option: SearchOption
): option is FuseResult<EntityTypeOption> => {
  return "label" in option.item;
};

// Component to render the list of recent objects
function RecentObjectsList({
  recentObjects,
  onSelect,
}: {
  recentObjects: RecentObject[];
  onSelect: () => void;
}) {
  if (recentObjects.length === 0) {
    return (
      <div className="p-4 text-center text-neutral-main text-sm">
        No recent items. Start browsing to see your history here.
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 px-4 py-2 text-xs text-neutral-main leading-none">
        <AccessTimeIcon fontSize="small" />
        Recent
      </div>

      <div className="max-h-80 overflow-y-auto scrollShadow">
        {recentObjects.map((item, index) => {
          return (
            <Link
              key={item.id || index}
              to={item.url}
              onClick={onSelect}
              className="flex items-center gap-3 px-4 py-2 hover:bg-action-hover cursor-pointer no-underline text-inherit"
            >
              <GlobalSearchAssetChip type={item.type} />

              <div className="flex flex-col flex-1 min-w-0">
                <span className="font-medium truncate">{item.displayName}</span>
                <span className="text-xs text-neutral-main truncate">
                  {item.parentContext ? `${item.parentContext} · ` : ""}
                  env: {item.env}
                  {item.pit ? ` · pit: ${item.pit}` : ""}
                </span>
              </div>

              <span className="text-xs text-neutral-main whitespace-nowrap">
                {formatDistanceToNow(item.visitedAt, { addSuffix: true })}
              </span>
            </Link>
          );
        })}
      </div>
    </>
  );
}

// Props for the row component (additional props passed via rowProps)
interface VirtualizedRowProps {
  items: React.ReactNode[];
}

// Row component for virtualized list
function VirtualizedRow({
  index,
  style,
  items,
}: {
  index: number;
  style: CSSProperties;
} & VirtualizedRowProps): ReactElement {
  const child = items[index] ?? null;
  if (!child) return <div style={style} />;
  return <div style={style}>{child}</div>;
}

// Virtualized listbox component
function VirtualizedListbox({
  children,
  ref,
  ...other
}: HTMLAttributes<HTMLElement> & {
  children: React.ReactNode[];
  ref?: React.Ref<HTMLDivElement>;
}) {
  const itemCount = Array.isArray(children) ? children.length : 0;
  const itemHeight = SEARCH_MODAL.ITEM_HEIGHT;
  const maxHeight = SEARCH_MODAL.LISTBOX_MAX_HEIGHT;

  return (
    <div
      ref={ref}
      {...other}
      style={{
        height: Math.min(itemCount * itemHeight, maxHeight),
        width: "100%",
      }}
    >
      <List<VirtualizedRowProps>
        rowComponent={VirtualizedRow}
        rowCount={itemCount}
        rowHeight={itemHeight}
        rowProps={{ items: children }}
        overscanCount={5}
        style={{
          height: "100%",
          width: "100%",
        }}
      />
    </div>
  );
}

const SearchModalRoot = ({ open, onClose }: SearchModalProps) => {
  useTrackFeature("global_search", open);

  const navigate = useNavigate();
  const {
    inputRef,
    searchQuery,
    setSearchQuery,
    selectedEntityType,
    setSelectedEntityType,
    entityTypeResults,
    entityResults,
    isLoading,
    recentObjects,
    showRecentObjects,
  } = useSearchModalLogic(open);

  // Combine entity types and entities for display (entity types first)
  const allOptions: SearchOption[] = [...entityTypeResults, ...entityResults];

  const handleOptionSelect = (
    _event: React.SyntheticEvent,
    value: SearchOption | null
  ) => {
    if (!value) return;

    if (isEntityTypeOption(value)) {
      // User selected an entity type
      setSelectedEntityType(value.item.type);
      setTimeout(() => {
        setSearchQuery("");
      });
    } else {
      // User selected an entity
      const url = getEntityUrl(value.item);
      if (url) {
        void navigate(url);
        onClose();
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Handle escape to close modal
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }

    // Handle backspace to remove entity type chip
    if (event.key === "Backspace" && selectedEntityType && searchQuery === "") {
      event.preventDefault();
      setSelectedEntityType(null);
    }
  };

  const getPlaceholderText = () =>
    selectedEntityType
      ? `Search ${selectedEntityType}s…`
      : "Search pipelines and tasks…";

  return (
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          timeout: 500,
          className: "backdrop-blur-xs",
        },
      }}
      className="flex items-start justify-center pt-32"
    >
      <div className="bg-background-paper rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <Autocomplete
          open={!showRecentObjects}
          value={null}
          options={allOptions}
          inputValue={searchQuery}
          onInputChange={(_event, newValue) => setSearchQuery(newValue)}
          onChange={handleOptionSelect}
          getOptionLabel={(option) =>
            isEntityTypeOption(option) ? option.item.label : option.item.name
          }
          getOptionKey={(option) =>
            isEntityTypeOption(option)
              ? `type-${option.item.type}`
              : `${option.item.type}-${option.item.name}-${option.item.payload.env_name}`
          }
          filterOptions={(x) => x}
          noOptionsText={isLoading ? "Loading..." : "No matches found"}
          slotProps={{
            listbox: {
              component: VirtualizedListbox,
              sx: {
                mt: 1,
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={inputRef}
              placeholder={getPlaceholderText()}
              variant="outlined"
              onKeyDown={handleKeyDown}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  endAdornment: isLoading ? (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  ) : null,
                  startAdornment: (
                    <>
                      <SearchIcon sx={{ ml: 1, mr: 0.5, color: "text.secondary" }} />
                      {selectedEntityType && (
                        <Chip
                          label={selectedEntityType}
                          size="small"
                          onDelete={() => setSelectedEntityType(null)}
                          deleteIcon={<CloseIcon />}
                          sx={{
                            ml: 0.5,
                            mr: 0.5,
                            height: "24px",
                          }}
                        />
                      )}
                      {params.slotProps.input?.startAdornment}
                    </>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "1.125rem",
                  "& fieldset": {
                    border: "none",
                  },
                },
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;

            if (isEntityTypeOption(option)) {
              const labelMatches = option.matches?.filter(
                (m: FuseResultMatch) => m.key === "label"
              );

              return (
                <li key={key} {...otherProps}>
                  <div className="flex items-center gap-3 w-full py-1 border-b border-grey-900">
                    <GlobalSearchAssetChip type={option.item.type} />
                    <span className="font-medium">
                      {highlightMatches(option.item.label, labelMatches)}
                    </span>
                    <span className="ml-auto text-xs text-grey-400">
                      Filter by type
                    </span>
                  </div>
                </li>
              );
            }

            const nameMatches = option.matches?.filter(
              (m: FuseResultMatch) => m.key === "name"
            );

            // Check if this is a task to display app name
            const isTask = option.item.type === "task";
            const taskPayload = isTask ? (option.item.payload as TaskPayload) : null;

            return (
              <li key={key} {...otherProps}>
                <div className="flex items-center gap-3 w-full py-1">
                  <GlobalSearchAssetChip type={option.item.type} />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-medium">
                      {highlightMatches(option.item.name, nameMatches)}
                    </span>
                    <span className="text-xs text-grey-400">
                      {isTask && taskPayload && `${taskPayload.app_name} · `}
                      env: {option.item.payload.env_name}
                    </span>
                  </div>
                </div>
              </li>
            );
          }}
        />
        {showRecentObjects && (
          <RecentObjectsList recentObjects={recentObjects} onSelect={onClose} />
        )}
      </div>
    </Modal>
  );
};

function SearchButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      onClick={onClick}
      startIcon={<SearchIcon />}
      sx={{
        color: "text.secondary",
        textTransform: "none",
        gap: 1,
        px: 2,
        mr: 1,
      }}
    >
      <span>search...</span>
      <span
        style={{
          fontSize: "0.75rem",
          opacity: 0.6,
          marginLeft: "auto",
        }}
      >
        ⌘K
      </span>
    </Button>
  );
}

export const SearchModal = Object.assign(SearchModalRoot, {
  useSearchModal,
  Button: SearchButton,
});
